import type { TFunction } from 'i18next';
import { type ConfigObject } from '../../../../_configs/types';
import type { Facet, FacetService, Facets, FacetsService } from '../../../../_types/Car';
import { arrAus } from '../../../../modules/fahrzeugsuche/filter/maps/arrAus';
import stringToSlug from '../../../../utils/string-to-slug';
import checkUndefinedNullOrEmpty from '../../../../utils/tools/checkUndefinedNullOrEmpty';

type ExtendedFacetsService = FacetsService & {
    ezSelect?: FacetService;
    ezAllSelect?: FacetService;
    kmSelect?: FacetService;
    kmAllSelect?: FacetService;
    leistungSelect?: FacetService;
    leistungAllSelect?: FacetService;
    preisSelect?: FacetService;
    preisAllSelect?: FacetService;
    rateSelect?: FacetService;
    rateSelectAll?: FacetService;
};

export default function handleFacets(fac: FacetsService, translate: TFunction, settings: ConfigObject): Facets {
    const noText: (keyof ExtendedFacetsService)[] = ['ez', 'ezAll'];
    const isTags: (keyof ExtendedFacetsService)[] = ['tags', 'tagsAll'];
    const formatNumber: (keyof Facets)[] = [
        'km',
        'kmAll',
        'kmSelect',
        'kmSelectAll',
        'Leistung',
        'LeistungAll',
        'leistungSelect',
        'leistungSelectAll',
        'preis',
        'preisAll',
        'preisSelect',
        'preisSelectAll',
        'rate',
        'rateAll',
        'rateSelect',
        'rateSelectAll',
    ];
    const sortFacetteById: (keyof Facets)[] = ['km', 'kmAll', 'Leistung', 'LeistungAll', 'preis', 'preisAll', 'rate', 'rateAll'];
    const rateAllFinanzierung = [];

    const facets: ExtendedFacetsService = { ...fac };
    if (!checkUndefinedNullOrEmpty(facets.getriebe) && checkUndefinedNullOrEmpty(facets.getriebe.Manuell)) {
        facets.getriebe.Manuell = facets.getriebe.manuell;
        delete facets.getriebe.manuell;
        facets.getriebeAll.Manuell = facets?.getriebeAll.manuell;
        delete facets.getriebeAll.manuell;
    }
    facets.aufbauten = facets?.aufbauAll || {};
    facets.ezSelect = facets?.ez || {};
    facets.ezAllSelect = facets?.ezAll || {};
    facets.kmSelect = facets?.km || {};
    facets.kmAllSelect = facets?.kmAll || {};
    facets.leistungSelect = facets.Leistung || {};
    facets.leistungAllSelect = facets.LeistungAll || {};
    facets.preisSelect = facets?.preis || {};
    facets.preisAllSelect = facets?.preisAll || {};
    facets.rateSelect = facets?.rate || {};
    facets.rateSelectAll = facets?.rateAll || {};
    const newFacets: Facets = {
        aus: arrAus(translate),
    };

    Object.keys(facets)
        ?.filter((facette) => ['ez', 'km', 'Leistung', 'preis', 'rate', 'ezAll', 'kmAll', 'LeistungAll', 'preisAll', 'rateAll'].includes(facette))
        .map(function (facette: any) {
            const newFacette: Facet[] = [];
            const facetElementKeys = Object.keys(facets?.[facette] || {})
                ?.filter((key) => key !== '(unbekannt)' && !checkUndefinedNullOrEmpty(facets?.[facette]?.[key]))
                ?.map((e) => parseInt(e?.replace(/(\.|,)/g, '')));
            const facetElementKeysAll = Object.keys(facets?.[`${facette}All`] || {})
                ?.filter((key) => key !== '(unbekannt)' && !checkUndefinedNullOrEmpty(facets?.[`${facette}All`]?.[key]))
                ?.map((e) => parseInt(e?.replace(/(\.|,)/g, '')));
            const rawMax: number = Math.max(...(!checkUndefinedNullOrEmpty(facetElementKeys) && facetElementKeys?.length > 1 ? facetElementKeys : facetElementKeysAll));
            const rawMin: number = Math.min(...(!checkUndefinedNullOrEmpty(facetElementKeys) && facetElementKeys?.length > 1 ? facetElementKeys : facetElementKeysAll));
            const max = rawMax >= 0 ? rawMax : 0;
            const min = rawMin >= 0 ? rawMin : 0;
            const step = facette === 'ez' || facette === 'Leistung' ? 1 : facette === 'km' ? 500 : 100;

            for (let i = min; i <= max; i = i + step) {
                newFacette.push({
                    i,
                    t: `${formatNumber.includes(facette) ? i?.toLocaleString('de-DE') : i?.toString()}${facette === 'ez' ? '' : ` ${facette === 'km' ? 'km' : facette === 'Leistung' || facette === 'LeistungAll' ? 'kW' : '€'}`}`,
                    tSlug: stringToSlug(i?.toString()),
                });
            }

            if (newFacette?.[0]?.i !== min) {
                newFacette.unshift({
                    i: min,
                    t: `${formatNumber.includes(facette) ? min?.toLocaleString('de-DE') : min?.toString()}${facette === 'ez' ? '' : ` ${facette === 'km' ? 'km' : facette === 'Leistung' || facette === 'LeistungAll' ? 'kW' : '€'}`}`,
                    tSlug: stringToSlug(min?.toString()),
                });
            }
            if (newFacette?.[newFacette?.length - 1]?.i !== max) {
                newFacette.push({
                    i: max,
                    t: `${formatNumber.includes(facette) ? max?.toLocaleString('de-DE') : max?.toString()}${facette === 'ez' ? '' : ` ${facette === 'km' ? 'km' : facette === 'Leistung' || facette === 'LeistungAll' ? 'kW' : '€'}`}`,
                    tSlug: stringToSlug(max?.toString()),
                });
            }

            const indexForStepLabels = [0, newFacette?.length - 1];
            newFacets[facette] = newFacette?.map((el, index) => ({ ...el, showLabel: indexForStepLabels.includes(index) }));
        });

    if (!checkUndefinedNullOrEmpty(newFacets.ez)) {
        const date = new Date();
        if (newFacets.ez.length === 0) {
            newFacets.ez.push(
                ...[
                    { i: date.getFullYear(), t: date.getFullYear()?.toString(), tSlug: stringToSlug(date.getFullYear()?.toString()) },
                    { i: date.getFullYear(), t: date.getFullYear()?.toString(), tSlug: stringToSlug(date.getFullYear()?.toString()) },
                ]
            );
        } else if (newFacets.ez.length === 1) {
            newFacets.ez.push({ i: date.getFullYear(), t: date.getFullYear()?.toString(), tSlug: stringToSlug(date.getFullYear()?.toString()) });
        }

        if (newFacets.ez?.[0]?.i === date.getFullYear()) {
            newFacets.ez[0].i = -2;
        }
    }

    if (!checkUndefinedNullOrEmpty(facets.rateAll)) {
        rateAllFinanzierung.push({ i: -1, t: translate('common:filter.alle') });
        const maxValue: number =
            !checkUndefinedNullOrEmpty(facets?.rateAll) &&
                !checkUndefinedNullOrEmpty(facets?.rateAll[Object.keys(facets?.rateAll).length - 1]) &&
                facets?.rateAll[Object.keys(facets?.rateAll).length - 1][0] as number <= 550
                ? facets?.rateAll[Object.keys(facets?.rateAll).length - 1][0] as number
                : 550;
        for (let i = 150; i <= maxValue + 100; i = i + 100) {
            if (i === 150) {
                rateAllFinanzierung.push({ i: [null, i].toString(), iSend: [null, i], t: `${translate('common:filter.to')} ${i} €` });
            } else if (i === maxValue + 100) {
                rateAllFinanzierung.push({ i: [maxValue, null].toString(), iSend: [maxValue, null], t: `${translate('common:filter.moreThan')} ${maxValue} €` });
            } else {
                rateAllFinanzierung.push({ i: [i - 100, i].toString(), iSend: [i - 100, i], t: `${i - 100} ${translate('common:filter.to')} ${i} €` });
            }
        }
        newFacets.rateAllFinanzierung = rateAllFinanzierung;
    }

    Object.keys(facets)
        ?.filter((facette) => ['firmen', 'standort', 'firmenAll', 'standortAll'].includes(facette))
        .map(function (facette: any) {
            const newFacette: Facet[] = [];
            const facettenData = settings?.general?.useFirmenFilter ? facets?.firmenData : facets?.standortData;
            Object.keys(facets[facette]).map(function (element) {
                const data = facettenData?.[facets?.[facette]?.[element]?.[0]] || {};
                if (facets[facette][element] && facets[facette][element][0] > -1) {
                    newFacette.push({
                        i: isTags.includes(facette) ? element : facets?.[facette]?.[element]?.[0],
                        t: noText.includes(facette) ? undefined : formatNumber.includes(facette) ? facets?.[facette]?.[element]?.[0]?.toLocaleString('de-DE') : element,
                        tSlug: stringToSlug(element),
                        et: '(' + facets[facette][element][1] + ')',
                        sd: !checkUndefinedNullOrEmpty(data) ? { ...data, resultCount: facets?.[facette]?.[element]?.[1] || 0 } : undefined,
                    });
                }
            });
            newFacets[facette] = newFacette;
        });

    Object.keys(facets)
        ?.filter((facette) => ['Ort', 'OrtAll'].includes(facette))
        .map(function (facette: any) {
            const newFacette: Facet[] = [];
            const facettenData = facets?.[facette];
            Object.keys(facets[facette]).map(function (element) {
                const data = facettenData?.[facets?.[facette]?.[element]?.[0]] || {};
                if (facets[facette][element] && facets[facette][element][0] !== '') {
                    newFacette.push({
                        i: stringToSlug(element), // "element" can contain special characters & Umlaute
                        t: element,
                        tSlug: stringToSlug(element),
                        et: '(' + facets[facette][element][1] + ')',
                        sd: !checkUndefinedNullOrEmpty(data) ? { ...data, resultCount: facets?.[facette]?.[element]?.[1] || 0 } : undefined,
                    });
                }
            });
            newFacets[facette] = newFacette;
        });


    Object.keys(facets)
        ?.filter((facette) => !['ez', 'firmen', 'Leistung', 'km', 'preis', 'rate', 'standort', 'Ort', 'ezAll', 'firmenAll', 'kmAll', 'LeistungAll', 'preisAll', 'rateAll', 'standortAll', 'OrtAll'].includes(facette))
        .map(function (facette: any) {
            const newFacette: Facet[] = [];
            Object.keys(facets[facette]).map(function (element) {
                if (facets[facette][element] && facets[facette][element][0] > -1) {
                    newFacette.push({
                        i: isTags.includes(facette) ? element : facets?.[facette]?.[element]?.[0],
                        t: noText.includes(facette)
                            ? undefined
                            : formatNumber.includes(facette)
                                ? facets?.[facette]?.[element]?.[0]?.toLocaleString('de-DE')
                                : element === 'CARAVAN' || element === 'KRAD' || element === 'REISEMOBIL' || element === 'TRANSPORTER'
                                    ? translate(`common:filter.kategorie${element}`)
                                    : element,
                        tSlug: stringToSlug(element),
                        et: '(' + facets[facette][element][1] + ')',
                    });
                }
            });
            newFacets[facette] = newFacette?.sort((a, b) => {
                if (sortFacetteById?.includes(facette) && typeof a?.i === 'number' && typeof b?.i === 'number') {
                    return a?.i - b?.i;
                } else {
                    return a?.t?.toLowerCase()?.localeCompare(b?.t?.toLowerCase());
                }
            });
        });

    return newFacets;
}
